(() => {
  const EVENT_PREFIX = 'choices_try_on_';
  const WAIT_COPY = 'Creating your personalized Choices preview. This can take 1–2 minutes per view. Please keep this window open.';

  function productHandle(widget) {
    return widget?.dataset.productHandle || location.pathname.split('/products/')[1]?.split(/[?#/]/)[0] || '';
  }

  function publish(name, widget, detail = {}) {
    const payload = {
      event: `${EVENT_PREFIX}${name}`,
      timestamp: new Date().toISOString(),
      path: location.pathname,
      product_handle: productHandle(widget),
      ...detail
    };

    document.dispatchEvent(new CustomEvent('choices:try-on-analytics', { detail: payload }));
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  }

  function parseSelectedVariant() {
    const script = document.querySelector('variant-selects [data-selected-variant], variant-radios [data-selected-variant]');
    try {
      return JSON.parse(script?.textContent || 'null');
    } catch (_error) {
      return null;
    }
  }

  function normalizeImageUrl(value) {
    if (!value) return '';
    try {
      return new URL(String(value), location.origin).href;
    } catch (_error) {
      return String(value);
    }
  }

  function activeProductImageUrl() {
    const selectors = [
      'media-gallery [data-media-id].is-active img',
      'media-gallery .is-active img',
      'media-gallery [aria-current="true"] img',
      '.product__media-item.is-active img',
      '.product__media img'
    ];
    for (const selector of selectors) {
      const image = document.querySelector(selector);
      const source = image?.currentSrc || image?.src;
      if (source) return normalizeImageUrl(source);
    }
    return '';
  }

  function selectedVariantImageUrl(variant) {
    return normalizeImageUrl(
      variant?.featured_media?.preview_image?.src ||
      variant?.featured_media?.preview_image?.url ||
      variant?.featured_image?.src ||
      variant?.featured_image?.url ||
      activeProductImageUrl()
    );
  }

  function currentVariantId() {
    return String(
      document.querySelector('.product-form input[name="id"], form[action*="/cart/add"] input[name="id"]')?.value ||
      parseSelectedVariant()?.id ||
      ''
    );
  }

  function clearStaleVariantResults(widget) {
    if (widget.resultsBySize && typeof widget.resultsBySize === 'object') widget.resultsBySize = {};
    if (widget.results && typeof widget.results === 'object') widget.results = {};
    widget.currentAngle = 'front';

    const resultImage = widget.querySelector('[data-result-image]');
    if (resultImage) {
      resultImage.removeAttribute('src');
      resultImage.hidden = true;
    }

    widget.querySelectorAll('[data-angle]').forEach((button) => {
      button.classList.remove('is-active', 'is-generated');
      button.setAttribute('aria-pressed', String(button.dataset.angle === 'front'));
    });

    try {
      const prefix = `choices-fitting-room:${location.pathname}:`;
      for (let index = sessionStorage.length - 1; index >= 0; index -= 1) {
        const key = sessionStorage.key(index);
        if (key?.startsWith(prefix)) sessionStorage.removeItem(key);
      }
    } catch (_error) {}
  }

  function syncVariant(widget, options = {}) {
    if (!widget) return;

    const variant = parseSelectedVariant();
    const variantId = String(variant?.id || currentVariantId());
    const imageUrl = selectedVariantImageUrl(variant);
    const previousVariantId = widget.dataset.selectedVariantId || '';
    const changed = Boolean(previousVariantId && variantId && previousVariantId !== variantId);

    if (changed) clearStaleVariantResults(widget);

    if (variantId) widget.dataset.selectedVariantId = variantId;
    if (variant?.title) widget.dataset.selectedVariantTitle = variant.title;
    if (imageUrl) {
      widget.dataset.garmentFrontImage = imageUrl;
      widget.dataset.garmentImage = imageUrl;
    }

    if (options.beforeOpen && !imageUrl) {
      console.warn('Choices fitting room could not resolve the selected variant image.');
    }
  }

  function syncAllVariants() {
    document.querySelectorAll('choices-try-on').forEach((widget) => syncVariant(widget));
  }

  function scheduleVariantSync() {
    window.setTimeout(syncAllVariants, 0);
    window.setTimeout(syncAllVariants, 180);
    window.setTimeout(syncAllVariants, 450);
  }

  function addPrivacyNote(widget) {
    if (widget.querySelector('[data-choices-privacy-note]')) return;
    const note = document.createElement('p');
    note.dataset.choicesPrivacyNote = 'true';
    note.className = 'choices-try-on__privacy';
    note.textContent = 'Your uploaded photo is used only to create this preview and is not retained after processing. Generated previews expire automatically after about two hours.';
    note.style.cssText = 'font-size:12px;line-height:1.45;opacity:.72;margin:.75rem 0;';

    const generateButton = widget.querySelector('[data-generate]');
    const upload = widget.querySelector('input[type="file"]');
    if (generateButton) generateButton.insertAdjacentElement('beforebegin', note);
    else if (upload) upload.insertAdjacentElement('afterend', note);
  }

  function controlBackView(widget) {
    const backButton = widget.querySelector('[data-angle="back"]');
    if (!backButton) return;

    const hasVerifiedBackImage = Boolean((widget.dataset.garmentBackImage || '').trim());
    backButton.hidden = !hasVerifiedBackImage;
    backButton.disabled = !hasVerifiedBackImage;
    backButton.setAttribute('aria-hidden', String(!hasVerifiedBackImage));

    if (hasVerifiedBackImage) {
      backButton.removeAttribute('title');
    } else {
      backButton.setAttribute('title', 'Back view unavailable because this product has no verified back image.');
    }
  }

  function setup(widget) {
    if (!widget) return;

    syncVariant(widget);
    addPrivacyNote(widget);
    controlBackView(widget);

    if (widget.dataset.productionTrackingReady === '1') return;
    widget.dataset.productionTrackingReady = '1';

    new MutationObserver(() => controlBackView(widget)).observe(widget, {
      attributes: true,
      attributeFilter: ['data-garment-back-image']
    });

    const loadingPanel = widget.querySelector('[data-panel="loading"]');
    const loadingMessage = widget.querySelector('[data-loading-message]');
    const resultPanel = widget.querySelector('[data-panel="result"]');
    const errorNode = widget.querySelector('[data-error]');

    widget.querySelector('[data-try-on-open]')?.addEventListener('click', () => {
      syncVariant(widget, { beforeOpen: true });
      publish('opened', widget, { variant_id: widget.dataset.selectedVariantId || '' });
    }, true);
    widget.querySelector('[data-generate]')?.addEventListener('click', () => {
      syncVariant(widget);
      publish('generation_started', widget, { view: 'front', variant_id: widget.dataset.selectedVariantId || '' });
    }, true);
    widget.querySelector('[data-regenerate]')?.addEventListener('click', () => {
      syncVariant(widget);
      publish('generation_started', widget, { view: 'front', regeneration: true, variant_id: widget.dataset.selectedVariantId || '' });
    }, true);
    widget.querySelectorAll('[data-angle]').forEach((button) => {
      button.addEventListener('click', () => publish('generation_started', widget, { view: button.dataset.angle || 'unknown' }));
    });
    widget.querySelector('[data-shop-product]')?.addEventListener('click', () => publish('add_to_cart_clicked', widget));

    const enforceWaitCopy = () => {
      if (loadingPanel && loadingMessage && !loadingPanel.hidden && loadingMessage.textContent !== WAIT_COPY) {
        loadingMessage.textContent = WAIT_COPY;
      }
    };

    if (loadingPanel && loadingMessage) {
      new MutationObserver(enforceWaitCopy).observe(loadingPanel, {
        attributes: true,
        attributeFilter: ['hidden']
      });
      new MutationObserver(enforceWaitCopy).observe(loadingMessage, {
        childList: true,
        characterData: true,
        subtree: true
      });
      enforceWaitCopy();
    }

    if (resultPanel) {
      new MutationObserver(() => {
        if (!resultPanel.hidden) publish('generation_completed', widget);
      }).observe(resultPanel, { attributes: true, attributeFilter: ['hidden'] });
    }

    if (errorNode) {
      new MutationObserver(() => {
        if (!errorNode.hidden && errorNode.textContent.trim()) {
          publish('generation_failed', widget, { message: errorNode.textContent.trim().slice(0, 180) });
        }
      }).observe(errorNode, { attributes: true, childList: true, subtree: true });
    }
  }

  function start() {
    document.querySelectorAll('choices-try-on').forEach(setup);
    new MutationObserver(() => document.querySelectorAll('choices-try-on').forEach(setup))
      .observe(document.documentElement, { childList: true, subtree: true });

    document.addEventListener('change', (event) => {
      if (event.target.matches?.('variant-selects input, variant-selects select, variant-radios input, variant-radios select, .product-form input[name="id"]')) {
        scheduleVariantSync();
      }
    }, true);

    try {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
        subscribe(PUB_SUB_EVENTS.variantChange, scheduleVariantSync);
      }
    } catch (_error) {}

    scheduleVariantSync();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
